import { useState } from 'react';
import { CreditCard } from 'lucide-react';

interface CardInputProps {
  onChange: (cardData: CardData) => void;
  onValidate?: (isValid: boolean) => void;
}

export interface CardData {
  number: string;
  holder_name: string;
  expiry_month: string;
  expiry_year: string;
  cvv: string;
}

export default function CardInput({ onChange, onValidate }: CardInputProps) {
  const [cardData, setCardData] = useState<CardData>({
    number: '',
    holder_name: '',
    expiry_month: '',
    expiry_year: '',
    cvv: ''
  });
  const [cardBrand, setCardBrand] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted.substring(0, 19); // 16 dígitos + 3 espaços
  };

  const formatCVV = (value: string) => {
    return value.replace(/\D/g, '').substring(0, 4);
  };

  const getCardBrand = (number: string) => {
    const cleaned = number.replace(/\D/g, '');
    
    if (/^4/.test(cleaned)) return 'visa';
    if (/^5[1-5]/.test(cleaned)) return 'mastercard';
    if (/^3[47]/.test(cleaned)) return 'amex';
    if (/^6(?:011|5)/.test(cleaned)) return 'discover';
    if (/^35/.test(cleaned)) return 'jcb';
    
    return '';
  };

  const validateCardNumber = (number: string) => {
    const digits = number.replace(/\D/g, '');
    if (digits.length < 13 || digits.length > 19) return false;

    let sum = 0;
    let isEven = false;

    for (let i = digits.length - 1; i >= 0; i--) {
      let digit = parseInt(digits[i]);

      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  };

  const handleCardNumberChange = (value: string) => {
    const formatted = formatCardNumber(value);
    const brand = getCardBrand(formatted);
    setCardBrand(brand);

    const newCardData = { ...cardData, number: formatted.replace(/\s/g, '') };
    setCardData(newCardData);
    onChange(newCardData);

    // Validar
    if (formatted.replace(/\s/g, '').length >= 13) {
      const isValid = validateCardNumber(formatted);
      setErrors({ ...errors, number: isValid ? '' : 'Número de cartão inválido' });
      onValidate?.(isValid);
    }
  };

  const handleExpiryChange = (field: 'month' | 'year', value: string) => {
    const cleaned = value.replace(/\D/g, '');
    
    if (field === 'month') {
      const month = cleaned.substring(0, 2);
      if (month && (parseInt(month) < 1 || parseInt(month) > 12)) {
        setErrors({ ...errors, expiry: 'Mês inválido' });
        return;
      }
      const newCardData = { ...cardData, expiry_month: month };
      setCardData(newCardData);
      onChange(newCardData);
    } else {
      const year = cleaned.substring(0, 4);
      const newCardData = { ...cardData, expiry_year: year };
      setCardData(newCardData);
      onChange(newCardData);
      
      // Validar ano
      if (year.length === 4) {
        const currentYear = new Date().getFullYear();
        if (parseInt(year) < currentYear) {
          setErrors({ ...errors, expiry: 'Cartão expirado' });
        } else {
          setErrors({ ...errors, expiry: '' });
        }
      }
    }
  };

  const handleCVVChange = (value: string) => {
    const formatted = formatCVV(value);
    const newCardData = { ...cardData, cvv: formatted };
    setCardData(newCardData);
    onChange(newCardData);
  };

  const handleHolderNameChange = (value: string) => {
    const newCardData = { ...cardData, holder_name: value.toUpperCase() };
    setCardData(newCardData);
    onChange(newCardData);
  };

  return (
    <div className="space-y-4">
      {/* Número do Cartão */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Número do Cartão *
        </label>
        <div className="relative">
          <input
            type="text"
            value={formatCardNumber(cardData.number)}
            onChange={(e) => handleCardNumberChange(e.target.value)}
            placeholder="1234 5678 9012 3456"
            className={`w-full border ${errors.number ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 pr-12`}
            required
          />
          <div className="absolute right-3 top-2.5">
            {cardBrand ? (
              <span className="text-xs font-semibold uppercase text-blue-600">{cardBrand}</span>
            ) : (
              <CreditCard className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>
        {errors.number && <p className="text-xs text-red-500 mt-1">{errors.number}</p>}
      </div>

      {/* Nome no Cartão */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nome no Cartão *
        </label>
        <input
          type="text"
          value={cardData.holder_name}
          onChange={(e) => handleHolderNameChange(e.target.value)}
          placeholder="NOME COMO ESTÁ NO CARTÃO"
          className="w-full border border-gray-300 rounded-md px-3 py-2 uppercase"
          required
        />
      </div>

      {/* Validade e CVV */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Validade *
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={cardData.expiry_month}
              onChange={(e) => handleExpiryChange('month', e.target.value)}
              placeholder="MM"
              maxLength={2}
              className={`w-full border ${errors.expiry ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-center`}
              required
            />
            <span className="text-2xl text-gray-400">/</span>
            <input
              type="text"
              value={cardData.expiry_year}
              onChange={(e) => handleExpiryChange('year', e.target.value)}
              placeholder="AAAA"
              maxLength={4}
              className={`w-full border ${errors.expiry ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-center`}
              required
            />
          </div>
          {errors.expiry && <p className="text-xs text-red-500 mt-1">{errors.expiry}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CVV *
          </label>
          <input
            type="text"
            value={cardData.cvv}
            onChange={(e) => handleCVVChange(e.target.value)}
            placeholder="123"
            maxLength={4}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-center"
            required
          />
          <p className="text-xs text-gray-500 mt-1">3 ou 4 dígitos no verso</p>
        </div>
      </div>
    </div>
  );
}
